import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class KvscheCareOffer extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.checkbox = this.querySelector('[data-cart-additional-item]');
    this.modalSelectButton = this.querySelector('[data-care-modal-select]');
    this.dialog = this.querySelector('dialog');
    this.dialogFrame = this.querySelector('.kv-care-brief__frame');
    this.dialogContent = this.querySelector('.kv-care-brief__content');
    this.slideViewport = this.querySelector('[data-care-slides]');
    this.slides = Array.from(this.querySelectorAll('[data-care-slide]'));
    this.previousSlideButton = this.querySelector('[data-care-slide-prev]');
    this.nextSlideButton = this.querySelector('[data-care-slide-next]');
    this.slideTargets = Array.from(this.querySelectorAll('[data-care-slide-target]'));
    this.slideCount = this.querySelector('[data-care-slide-count]');
    this.currentSlide = 0;
    this.status = this.querySelector('[data-care-status]');
    this.productForm = this.closest('product-form-component');
    this.form = this.closest('form');
    this.buttonLabel = this.productForm?.querySelector('.add-to-cart-text__content > span > span');

    if (!this.checkbox || !this.productForm || !this.form) return;

    const signal = this.abortController.signal;
    this.checkbox.addEventListener('change', this.updateSelection, { signal });
    this.modalSelectButton?.addEventListener('click', this.selectFromBrief, { signal });
    this.productForm.addEventListener('input', this.updateQuantity, { signal });
    this.productForm.addEventListener('change', this.updateQuantity, { signal });
    this.productForm.addEventListener('quantity-selector:update', this.updateQuantity, { signal });
    this.previousSlideButton?.addEventListener('click', () => this.showSlide(this.currentSlide - 1), { signal });
    this.nextSlideButton?.addEventListener('click', () => this.showSlide(this.currentSlide + 1), { signal });
    this.slideTargets.forEach((button) => {
      button.addEventListener('click', () => this.showSlide(Number(button.dataset.careSlideTarget)), { signal });
    });
    this.form.addEventListener('submit', this.handleSubmit, { signal });
    this.productForm.addEventListener('kvsche:additional-item-error', this.handleAdditionalItemError, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, { signal });
    window.addEventListener('pageshow', this.updateSelection, { signal });
    this.showSlide(0);
    this.updateSelection();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  showSlide(index) {
    if (!this.slides.length) return;

    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.currentSlide = nextIndex;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      slide.hidden = !isActive;
      slide.classList.toggle('is-active', isActive);
    });

    this.slideTargets.forEach((button, buttonIndex) => {
      button.classList.toggle('is-active', buttonIndex === nextIndex);
    });

    if (this.previousSlideButton) this.previousSlideButton.disabled = nextIndex === 0;
    if (this.nextSlideButton) this.nextSlideButton.disabled = nextIndex === this.slides.length - 1;
    if (this.slideCount) {
      this.slideCount.textContent = `${String(nextIndex + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    }

    if (this.slideViewport) this.slideViewport.scrollTop = 0;
  }

  updateQuantity = () => {
    const input = this.productForm?.querySelector('[name="quantity"]');
    const quantity = Math.max(1, Number(input?.value || this.productForm?.dataset.quantityDefault) || 1);
    const total = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD', currencyDisplay: 'code',
    }).format((Number(this.dataset.unitPrice) * quantity) / 100);
    if (this.modalSelectButton) {
      this.modalSelectButton.textContent = (this.dataset.quantityButton || '')
        .replace('__quantity__', String(quantity)).replace('__total__', total);
    }
    this.querySelectorAll('[data-care-quantity-summary]').forEach((element) => {
      element.textContent = (this.dataset.quantityTemplate || '')
        .replace('__quantity__', String(quantity))
        .replace('__price__', this.dataset.unitLabel || '')
        .replace('__total__', total);
    });
  };

  updateSelection = () => {
    this.updateQuantity();
    this.classList.toggle('is-selected', this.checkbox?.checked === true);
    this.setButtonLabel(this.checkbox?.checked ? this.dataset.selectedLabel : this.dataset.defaultLabel);
  };

  selectFromBrief = async (event) => {
    if (!this.checkbox) return;

    this.checkbox.checked = true;
    this.updateSelection();

    const dialogComponent = event.currentTarget.closest('dialog-component');
    if (dialogComponent?.closeDialog) await dialogComponent.closeDialog();
    this.checkbox.focus({ preventScroll: true });
  };

  handleSubmit = () => {
    if (!this.checkbox?.checked) return;
    this.setButtonLabel(this.dataset.addingLabel);
    if (this.status) this.status.textContent = this.dataset.addingLabel || '';
  };

  handleAdditionalItemError = (event) => {
    if (this.checkbox) this.checkbox.checked = false;
    this.updateSelection();
    if (this.status) this.status.textContent = event.detail?.message || '';
  };

  handleCartUpdate = (event) => {
    if (event.target !== this.productForm || !event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (detail?.didError || !this.checkbox?.checked) {
          this.updateSelection();
          return;
        }

        this.checkbox.checked = false;
        this.updateSelection();
        if (this.status) this.status.textContent = this.dataset.addedMessage || '';
      })
      .catch(() => this.updateSelection());
  };

  setButtonLabel(label) {
    if (this.buttonLabel && label) this.buttonLabel.textContent = label;
  }
}

class KvscheCareCartOffer extends HTMLElement {
  static pending = false;
  connectedCallback() {
    this.abortController = new AbortController();
    this.button = this.querySelector('[data-care-cart-add]');
    this.status = this.querySelector('[data-care-cart-status]');
    this.defaultLabel = this.button?.textContent?.trim() || '';
    this.button?.addEventListener('click', this.addCare, { signal: this.abortController.signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(({ detail }) => {
        if (Array.isArray(detail?.items)) this.reconcile(detail.items);
      }).catch(() => {});
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  reconcile(items) {
    const eligible = items.filter((item) => Number(item.product_id) === Number(this.dataset.eligibleProductId))
      .reduce((total, item) => total + item.quantity, 0);
    const care = items.filter((item) => Number(item.product_id) === Number(this.dataset.careProductId))
      .reduce((total, item) => total + item.quantity, 0);
    const needed = Math.max(0, eligible - care);
    if (needed !== Number(this.dataset.quantity)) {
      if (this.button) this.button.hidden = true;
      this.querySelector('.kv-care-quantity')?.setAttribute('hidden', '');
      const link = this.querySelector('[data-care-view-cart]');
      if (link) link.hidden = false;
    }
    return needed;
  }

  addCare = async () => {
    const variantId = Number(this.dataset.variantId);
    const quantity = Number(this.dataset.quantity);
    if (!this.button || !variantId || !quantity || KvscheCareCartOffer.pending) return;
    KvscheCareCartOffer.pending = true;

    this.button.disabled = true;
    this.button.textContent = this.dataset.addingLabel || this.defaultLabel;
    if (this.status) this.status.textContent = this.dataset.addingLabel || '';

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      sectionIds.add(component.dataset.sectionId);
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const currentResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin', headers: { Accept: 'application/json' },
      });
      if (!currentResponse.ok) throw new Error(this.dataset.errorMessage);
      const current = await currentResponse.json();
      if (this.reconcile(current.items) !== quantity) {
        throw new Error(this.dataset.changedMessage);
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorMessage);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error(this.dataset.errorMessage);
      const cart = await cartResponse.json();
      document.querySelectorAll('kvsche-care-cart-offer').forEach((offer) => offer.reconcile(cart.items));

      if (this.status) this.status.textContent = this.dataset.addedMessage || '';
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: result.sections,
          source: 'kvsche-care-cart-offer',
          didError: false,
        },
      });

      window.setTimeout(() => {
        document.querySelector('[data-kvsche-care-line] .cart-items__title')?.focus();
      }, 160);
    } catch (error) {
      const message = error?.message || this.dataset.errorMessage || '';
      if (this.status) this.status.textContent = message;
      this.button.disabled = false;
      this.button.textContent = this.defaultLabel;
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      KvscheCareCartOffer.pending = false;
    }
  };
}

class KvscheCareCartIntegrity extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.button = this.querySelector('[data-care-integrity-fix]');
    this.status = this.querySelector('[data-care-integrity-status]');
    this.stateElement = this.querySelector('[data-care-integrity-state]');
    this.defaultLabel = this.button?.textContent?.trim() || '';
    this.button?.addEventListener('click', this.correctCart, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  getState() {
    try {
      return JSON.parse(this.stateElement?.textContent || '{}');
    } catch {
      return {};
    }
  }

  correctCart = async () => {
    if (!this.button || this.button.disabled) return;

    const state = this.getState();
    const careLines = Array.isArray(state.careLines) ? state.careLines : [];
    if (careLines.length === 0) return;

    const removeCare = this.dataset.issue === 'care_only' || this.dataset.issue === 'unsupported_country';
    const targetQuantity = removeCare
      ? 0
      : Math.min(Number(state.careQuantity) || 0, Number(state.eligibleQuantity) || 0);
    const updates = {};

    careLines.forEach((line, index) => {
      if (!line?.key) return;
      updates[line.key] = index === 0 ? targetQuantity : 0;
    });

    this.button.disabled = true;
    this.button.textContent = this.dataset.fixingLabel || this.defaultLabel;
    if (this.status) this.status.textContent = this.dataset.fixingLabel || '';

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      sectionIds.add(component.dataset.sectionId);
    });

    const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: targetQuantity === 0 ? 'remove' : 'update',
        context: 'cart',
        lines,
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates,
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || this.dataset.errorMessage);
      }

      if (this.status) this.status.textContent = this.dataset.fixedMessage || '';
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          items: result.items,
          itemCount: result.item_count,
          sections: result.sections,
          source: 'kvsche-care-cart-integrity',
          didError: false,
        },
      });

      window.setTimeout(() => {
        const focusRoot = document.querySelector('dialog[open]') || document;
        focusRoot.querySelector('.cart__checkout-button:not(:disabled)')?.focus();
      }, 160);
    } catch (error) {
      const message = error?.message || this.dataset.errorMessage || '';
      if (this.status) this.status.textContent = message;
      this.button.disabled = false;
      this.button.textContent = this.defaultLabel;
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );
    }
  };
}

if (!customElements.get('kvsche-care-offer')) {
  customElements.define('kvsche-care-offer', KvscheCareOffer);
}

if (!customElements.get('kvsche-care-cart-offer')) {
  customElements.define('kvsche-care-cart-offer', KvscheCareCartOffer);
}

if (!customElements.get('kvsche-care-cart-integrity')) {
  customElements.define('kvsche-care-cart-integrity', KvscheCareCartIntegrity);
}
